import type { Dispatch } from '@reduxjs/toolkit';

import { OrganizationSettings } from '../../../types';
import { SET_TOAST_DURATION_IN_MILLISECONDS } from '../../../utils/setToastDuration';
import TCClient from 'client';
import { actions, store, thunk } from 'redux-stores';
import { AuthSaml } from 'js-sdk/src/services/Admin/utils/types';

export const findSettings = async (organizationId: string) => {
  const settings = (await TCClient.adminOrganizations.fetchSettings({
    organizationId: organizationId,
  })) as OrganizationSettings;
  return settings;
};

export const fetchSettings = async (dispatch: Dispatch) => {
  const {
    admin: { selectedOrganizationId },
  } = store.getState();

  const settings = await findSettings(selectedOrganizationId);
  dispatch(actions.updateSettings(settings));
};

export const saveSettings = async ({
  dispatch,
  location = 'root',
  logGainsightAnalytics,
}: {
  logGainsightAnalytics?: (p: { tracker: { name: string } }) => void;
  location?: string;
  dispatch: Dispatch;
}) => {
  const {
    admin: { orgSettingsTabSaveFunc },
  } = store.getState();

  try {
    const {
      admin: { selectedOrganizationId },
      orgSettings: { settings },
    } = store.getState();

    if (settings.authType === 'saml' && location === 'authentication') {
      // Validate required SAML fields
      if (!settings.authSamlIspMetadataUrl) {
        dispatch(actions.setValidationError('emptyAuthSamlIspMetadataUrl'));
        return;
      }

      const samlPayload = {
        organization_id: selectedOrganizationId,
        auth: {
          auth_type: settings.authType,
          saml_idp_metadata: settings.authSamlIspMetadataUrl,
          saml_trusted_fingerprint: settings.authSamlIspFingerprint,
          auth_saml_assertion_user_id: settings.authSamlAssertionId,
          auth_saml_tigertext_user_id: settings.authSamlTcId,
          saml_session_ttl: settings.authSamlTtl,
        } as AuthSaml,
      };
      await TCClient.adminOrganizations.saveAuthSettings({
        data: samlPayload,
      });
    } else {
      await TCClient.adminOrganizations.saveSettings({
        organizationId: selectedOrganizationId,
        settings,
        logGainsightAnalytics,
      });
    }

    if (orgSettingsTabSaveFunc) {
      await orgSettingsTabSaveFunc();
    }

    await fetchSettings(dispatch);

    await TCClient.organizations.refreshAll();

    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(3),
      open: true,
      message: 'Organization settings have been saved.',
      type: 'SUCCESS',
    });
  } catch (error: unknown) {
    let errorMessage = "An error occurred when saving the organization's settings. ";

    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'body' in error.response &&
      error.response.body &&
      typeof error.response.body === 'object'
    ) {
      const responseBody = error.response.body as {
        status?: string;
        error?: {
          message?: string;
          type?: string;
        };
      };
      if (responseBody.status === 'fail' && responseBody.error?.message) {
        // Handle specific SAML Metadata URL / fingerprint errors
        const lowerMessage = responseBody.error.message.toLowerCase();
        if (
          lowerMessage.includes(
            'could not set an idp for organization: trusted_fingerprint is required'
          ) ||
          lowerMessage.includes('metadata')
        ) {
          dispatch(actions.setValidationError('invalidAuthSamlIspMetadataUrl'));
          return;
        } else {
          errorMessage += capitalizeEachWord(responseBody.error?.message);
        }
      }
    }
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
      open: true,
      message: errorMessage,
      type: 'FAILURE',
    });
  }
};

export const deleteOrganization = async (dispatch: Dispatch) => {
  const {
    admin: { selectedOrganizationId },
  } = store.getState();

  await TCClient.adminOrganizations.deleteOrganization({
    organizationId: selectedOrganizationId,
  });

  dispatch(actions.removeOrganization({ organizationId: selectedOrganizationId }));
};

export const createDirectory = async (_dispatch: Dispatch, directory: string) => {
  const {
    admin: { selectedOrganizationId },
  } = store.getState();

  return TCClient.adminOrganizations.createCustomDirectories({
    organizationId: selectedOrganizationId,
    directories: [directory],
  });
};

export const deleteDirectory = async (_dispatch: Dispatch, directory: string) => {
  const {
    admin: { selectedOrganizationId },
  } = store.getState();

  return TCClient.adminOrganizations.deleteCustomDirectories({
    organizationId: selectedOrganizationId,
    directories: [directory],
  });
};

export const fetchDirectories = async (_dispatch?: Dispatch) => {
  const {
    admin: { selectedOrganizationId },
  } = store.getState();
  const directories = await TCClient.adminOrganizations.fetchCustomDirectories({
    organizationId: selectedOrganizationId,
  });

  return directories;
};

export const findUnits = async (_dispatch?: Dispatch) => {
  const {
    admin: { selectedOrganizationId },
  } = store.getState();

  return await TCClient.multiOrg.findUnits({
    orgId: selectedOrganizationId,
  });
};

export const capitalizeEachWord = (sentence: string): string => {
  return sentence
    .toLowerCase()
    .split(' ')
    .map((word) => {
      if (word.length === 0) {
        return '';
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};
